import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl } from '../../lib/ep.js';

import { MainButton, SecondaryButton } from '../builder/ActionButton';
import ButtonCollection from '../builder/ButtonCollection.js';
import Tooltip from '../builder/Tooltip.js';

const SingleItemDefault = ({ item, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ button_label, setButtonLabel ] = useState(item?.cta_label ?? "Select");

	useEffect(() => {
		setButtonLabel(item?.cta_label ?? "Select");
	}, [ item.cta_label ]);

	const is_disabled = item?.disabled ?? false;
	const disabled_reason = (is_disabled) ? (item?.dis_reason ?? false) : false;

	let Component = MainButton;
	if(item.cta_type != undefined) {
		switch(item.cta_type) {
			case 'secondary':
				Component = SecondaryButton;
				break;
		}
	}

	return (
		<>
			<div class={item?.icon ?? 'knk-arrow-r-big'}>
				<h4>{i18n(item.name)}</h4>

				{(item.desc != undefined) && (
					<p>{i18n(item.desc)}</p>
				)}
			</div>

			{(disabled_reason) && (
				<Tooltip parent_classes='is_fullscreen relative_to_container' with_icon={false} position='top'>{disabled_reason}</Tooltip>
			)}

			<ButtonCollection>
				{(item.cta_whole != undefined) ? (
					item.cta_whole
				) : (
					<Component
						_class={item?.cta_classes ?? null}
					>{button_label}</Component>
				)}
			</ButtonCollection>
		</>
	)
};

/**
 * @param items			[array]		[mand.]	[array of objects containing each item to display in the selector. Accepted attributes below:]
 *		@ name			[string]	[mand.]	[short text to be displayed to identify the item]
 *		@ desc			[string]	[opt.]	[long text added under the name attribute]
 *		@ icon			[string]	[opt.]	[KNk / EP icon to be added left to the name attribute. "knk-arrow-r-big"if missing]
 *		@ cta_label		[string]	[opt.]	[label for the specific CTA button. "Select" if missing]
 *		@ cta_classes	[object]	[opt.]	[class object for the specific CTA button. None if missing]
 *		@ cta_whole		[object]	[opt.]	[replaces the whole CTA button with this item. Default CTA button if missing]
 *		@ disabled		[boolean]	[opt.]	[indicates if an item is disabled or not]
 *		@ dis_reason	[text]		[opt.]	[text to display why the item is disabled. Only applies if disabled is true]
 *		@ onClick		[function]	[opt.]	[function after clicking the item. Only if generic function is missing]
 *		@ url			[string]	[opt.]	[url to be opened when clicked the item. Only if "onClick" attribute is missing]
 *		@ target		[string]	[opt.]	[target of the link, only applies if "url" attribute is present]
 *		@ props			[object]	[opt.]	[other additional props passed to the item]

 * @param onSelectItem	[function]	[opt.]	[generic function when clicking any item. Can be overwritten with specific behavior for each item]

 * @param extra_classes	[string]	[opt.]	[additional classes to be added to the general container]

 * @param is_margined	[string]	[opt.]	[if missing or false, additional class "not margined" will be added to the general container]

 * @returns list of items
 */
const IconedSelectorItems = ({ items, ...props }) => {
	const navigateToUrl = (url, target) => {
		if(target == undefined) target = '_self';

		switch(target) {
			case '_blank':
			case '_new':
				window.open(url, '_blank');
				break;

			case '_top':
			case '_parent':
				parent.location.href = url;
				break;

			case '_self':
			default:
				window.location.href = url;
				break;
		}
	};

	const selectItem = (item) => {
		if(props.onSelectItem != undefined) {
			props.onSelectItem(item);
		} else if(item.onClick != undefined) {
			item.onClick();
		} else if(item.url != undefined) {
			navigateToUrl(item.url, item.target);
		} else {
			console.error("WARNING: onSelectItem() function is not defined");
		}
	};

	let _classes = ['gui_iconed_selector'];
	if(props.extra_classes != undefined) _classes.push(props.extra_classes);
	if(props.is_margined == undefined || !props.is_margined) _classes.push('not_margined');

	const selected_item = props?.selected_item ?? false;

	return (
		<ul class={_classes.join(" ")}>
			{items.map((_item, i) => {
				let item = { ..._item };

				const skip = item?.skip ?? false;
				if(skip) return null;

				const is_disabled = item?.disabled ?? false;
				const item_key = item?.key ?? '';

				let item_classes = ['is_linked_element'];
				if(is_disabled) item_classes.push('is_disabled');

				if(item_key && selected_item !== false && item_key == selected_item) {
					item_classes.push('selected');
					item.cta_label = item.cta_label_selected;
				}

				const styles = item?.styles ?? null;

				return (
					<li
						key={`item_${i}`}
						class={item_classes.join(" ")}
						onClick={(!is_disabled) ? () => selectItem(item) : null}
						style={styles}
					>
						{(props.children != undefined) ? (
							React.cloneElement(props.children, { item })
						) : (
							<SingleItemDefault
								item={item}
								intl={props.intl}
							/>
						)}
					</li>
				)
			})}
		</ul>
	)
};

export default injectIntl(IconedSelectorItems);